// src/pages/portal/PortalRecordPage.js — Standalone pages for a contact
// (/portal/contacts/:id) and a firm (/portal/firms/:id), the "Open full
// page" target of the portal peek. The partner's version of the CRM's person
// and firm pages (Travis, 11/3), in the portal mandate page's boxes: what the
// partner can already see (their own pipeline attribution, via
// /api/portal/my-investors) plus a firm's public facts (/api/portal/firms/:id,
// RLS-scoped). RGA's notes, owners, research and activity never appear.
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../../lib/api'
import { PortalPeekLink, usePortalDirectory } from '../../components/portal/PortalPeek'
import { PL_BADGE } from '../../lib/pipelineStages'
import { PORTAL_PAGE_TITLE_STYLE } from '../../lib/portalPageTitleStyle'

const list = v => [].concat(v ?? []).filter(x => x != null && x !== '')
const formatCurrency = n => (n == null ? '—' : `$${Number(n).toLocaleString()}`)
const formatDate = d => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—')

const TILE_HEADING = {
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--text-muted)', marginBottom: 10,
}

function Tile({ title, span = 2, children }) {
  return (
    <div className={`portal-tile span-${span}`}>
      <div style={TILE_HEADING}>{title}</div>
      {children}
    </div>
  )
}

function Facts({ rows }) {
  const shown = rows.filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length))
  if (!shown.length) return <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Nothing on file.</div>
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 18px', fontSize: 14 }}>
      {shown.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div style={{ color: 'var(--text-muted)' }}>{k}</div>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{v}</div>
        </div>
      ))}
    </div>
  )
}

// The partner's pipeline rows for this record: one per mandate (and person,
// on a firm page).
function MandatesTable({ rows, showContact }) {
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Mandate</th>
            {showContact && <th>Contact</th>}
            <th>Stage</th>
            <th>Target</th>
            <th>Committed</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.pipeline_id}>
              <td style={{ fontWeight: 500 }}><Link to={`/portal/mandates/${r.mandate_id}`}>{r.mandate_name}</Link></td>
              {showContact && (
                <td>{r.contact_id
                  ? <PortalPeekLink type="contact" id={r.contact_id}>{r.investor_contact_name}</PortalPeekLink>
                  : '—'}</td>
              )}
              <td><span className={`badge ${PL_BADGE[r.stage] ?? 'badge-gray'}`}>{r.stage}</span></td>
              <td>{formatCurrency(r.target_commitment)}</td>
              <td>{formatCurrency(r.actual_commitment)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.last_activity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Page({ children }) {
  const navigate = useNavigate()
  return (
    <div className="page">
      <button type="button" onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13,
          color: 'var(--text-secondary)', cursor: 'pointer', marginBottom: 12 }}>
        ← Back
      </button>
      {children}
    </div>
  )
}

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner spinner-lg" /></div>
}

function NotYours({ what }) {
  return (
    <div className="empty-state">
      <h3>This {what} isn't among your contacts</h3>
      <p><Link to="/portal/contacts">Go to Contacts</Link></p>
    </div>
  )
}

export function PortalContactPage() {
  const { id } = useParams()
  const { data, isLoading } = usePortalDirectory()
  if (isLoading) return <Page><Loading /></Page>
  const rows = (data ?? []).filter(r => r.contact_id === id)
  if (!rows.length) return <Page><NotYours what="contact" /></Page>

  const c = rows[0]
  const firms = [...new Map(rows.filter(r => r.investor_firm_id)
    .map(r => [r.investor_firm_id, r.investor_firm_name])).entries()]

  return (
    <Page>
      <div className="page-header">
        <div className="page-header-text">
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>{c.investor_contact_name}</h1>
        </div>
      </div>
      <div className="portal-tiles">
        <Tile title="Contact">
          <Facts rows={[
            ['Title', c.title_role],
            ['Firm', firms.map(([fid, name]) => (
              <div key={fid}><PortalPeekLink type="firm" id={fid}>{name}</PortalPeekLink></div>
            ))],
            ['Email', list(c.email).map(e => <div key={e}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>)],
            ['Phone', list(c.phone).join(', ')],
          ]} />
        </Tile>
        <Tile title="With you">
          <Facts rows={[
            ['Mandates', rows.length],
            ['Last activity', formatDate(rows.map(r => r.last_activity).filter(Boolean).sort().pop())],
          ]} />
        </Tile>
        <Tile title="Your mandates with this contact" span={4}>
          <MandatesTable rows={rows} />
        </Tile>
      </div>
    </Page>
  )
}

export function PortalFirmPage() {
  const { id } = useParams()
  const { data, isLoading } = usePortalDirectory()
  const { data: firm } = useQuery({
    queryKey: ['portalFirm', id],
    queryFn: () => api.get(`/api/portal/firms/${id}`).then(r => r.data.data),
    retry: false,
  })
  if (isLoading) return <Page><Loading /></Page>
  const rows = (data ?? []).filter(r => r.investor_firm_id === id)
  if (!rows.length) return <Page><NotYours what="firm" /></Page>

  const people = [...new Map(rows.filter(r => r.contact_id).map(r => [r.contact_id, r])).values()]
  const website = firm?.website && (firm.website.startsWith('http') ? firm.website : `https://${firm.website}`)

  return (
    <Page>
      <div className="page-header">
        <div className="page-header-text">
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>{firm?.name ?? rows[0].investor_firm_name}</h1>
        </div>
      </div>
      <div className="portal-tiles">
        <Tile title="Firm">
          <Facts rows={[
            ['Type', list(firm?.firm_type).join(', ')],
            ['Subtype', firm?.investor_subtype],
            ['Headquarters', firm?.hq_location],
            ['Website', website && <a href={website} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--accent)' }}>{firm.website}</a>],
          ]} />
        </Tile>
        <Tile title={`Your contacts here (${people.length})`}>
          {!people.length ? <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>No named contacts.</div>
            : people.map(p => (
              <div key={p.contact_id} style={{ fontSize: 14, marginBottom: 6 }}>
                <PortalPeekLink type="contact" id={p.contact_id}>{p.investor_contact_name}</PortalPeekLink>
                {p.title_role && <span style={{ color: 'var(--text-muted)' }}> · {p.title_role}</span>}
              </div>
            ))}
        </Tile>
        <Tile title="Your mandates with this firm" span={4}>
          <MandatesTable rows={rows} showContact />
        </Tile>
      </div>
    </Page>
  )
}
